import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { ChevronRightIcon } from '@treams/treams-icons';
import * as graphql from '@treams/treams-network';
import { Accordion } from '@treams/treams-ui/common/Accordion';
import { Avatar } from '@treams/treams-ui/common/Avatar';
import { Button } from '@treams/treams-ui/common/button/Button';
import { Card } from '@treams/treams-ui/common/Card';
import { Empty } from '@treams/treams-ui/common/Empty';
import { FlexGap } from '@treams/treams-ui/common/FlexGap';
import { SegmentedControl } from '@treams/treams-ui/common/SegmentedControl';
import { Heading3, Label, Paragraph } from '@treams/treams-ui/common/Text';
import {
	ArrowDownIcon,
	ArrowUpIcon,
	CashIcon,
	DoubleChevronDownIcon,
	DoubleChevronUpIcon,
} from '@treams/treams-ui/icons';
import { tokens } from '@treams/treams-ui/theme/tokens';

import { formatSalary } from '../../salary-development/formatSalary';
import {
	type RewardEmployee,
	type RewardFunction,
	type RewardRange,
	type RewardSort,
	type SalaryDomain,
	compareRewardFunctions,
	salaryDomain,
} from './buildRewardFunctions';
import { matchRewardRow, personMatches } from './rewardSearch';
import { SALARY_PLOT_HEIGHT, SalaryRangeBar, outsideSalaryNote } from './SalaryRangeBar';

const FunctionRow = styled(FlexGap)`
	width: 100%;

	&:hover {
		background: ${tokens.surface.hover};
	}
`;

const FunctionRows = styled(Box)`
	button {
		display: flex;
	}
`;

export function ColumnHeader({ labels }: { labels: [string, string] }) {
	return (
		<FlexGap
			mt={3}
			mx={-3}
			px={3}
			alignItems="center"
			gap={tokens.gap.list}
			minHeight={44}
			style={{
				background: tokens.surface.primary,
				borderTop: `1px solid ${tokens.border.subtle}`,
				borderBottom: `1px solid ${tokens.border.subtle}`,
			}}
		>
			<Box width={16} flexShrink={0} />
			<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
				<Paragraph small>{labels[0]}</Paragraph>
			</Box>
			<Box flex="2 1 220px" minWidth={0}>
				<Paragraph small>{labels[1]}</Paragraph>
			</Box>
		</FlexGap>
	);
}

function RangeLabel({ range }: { range?: RewardRange }) {
	const { i18n } = useTranslation();

	if (range?.startAmount == null || range.endAmount == null) {
		return null;
	}

	const amounts = `${formatSalary(range.startAmount, i18n.language)} – ${formatSalary(range.endAmount, i18n.language)}`;
	const name = range.name?.trim();

	return (
		<Paragraph small mt={2}>
			{name ? `${name} (${amounts})` : amounts}
		</Paragraph>
	);
}

// Average full-time salary of everyone with a salary; for one person, their own.
function SalaryLabel({ employee }: { employee: RewardEmployee }) {
	const { i18n, t } = useTranslation();

	if (employee.salary == null) {
		return null;
	}

	const money = (amount: number) => formatSalary(Math.round(amount), i18n.language);
	const note = outsideSalaryNote(employee.salary, employee.range, money, t);

	return (
		<FlexGap alignItems="center" gap={tokens.gap.element} flexWrap="wrap">
			<FlexGap alignItems="center" gap={tokens.gap.inline}>
				<Box aria-hidden style={{ display: 'flex', color: tokens.icon.tertiary }}>
					<CashIcon size={14} />
				</Box>
				<Paragraph small>{money(employee.salary)}</Paragraph>
			</FlexGap>
			{note && (
				<Paragraph small style={{ color: tokens.warning.text }}>
					{note}
				</Paragraph>
			)}
		</FlexGap>
	);
}

function RangeColumn({
	label,
	range,
	employees,
	domain,
	emptyLabel,
	fullTimeLabel,
}: {
	label: string;
	range?: RewardFunction['range'];
	employees: RewardEmployee[];
	domain: SalaryDomain;
	emptyLabel: string;
	fullTimeLabel?: string;
}) {
	const people = employees.flatMap((employee) =>
		employee.salary == null ? [] : [{ ...employee, salary: employee.salary }],
	);

	if (people.length === 0) {
		return (
			<FlexGap
				flex="2 1 220px"
				minWidth={0}
				alignItems="center"
				justifyContent="center"
				style={{ minHeight: SALARY_PLOT_HEIGHT }}
			>
				<Paragraph small style={{ color: tokens.text.tertiary }}>
					{emptyLabel}
				</Paragraph>
			</FlexGap>
		);
	}

	return (
		<Box flex="2 1 220px" minWidth={0}>
			<SalaryRangeBar
				label={label}
				range={range}
				people={people}
				domain={domain}
				fullTimeLabel={fullTimeLabel}
			/>
		</Box>
	);
}

export function RewardFunctions({
	functions,
	query = '',
}: {
	functions: RewardFunction[];
	query?: string;
}) {
	const { i18n, t } = useTranslation();
	const [open, setOpen] = useState<Record<string, boolean>>({});
	const [openQuery, setOpenQuery] = useState(query);

	// A new search decides again which rows open; earlier toggles would otherwise win.
	if (openQuery !== query) {
		setOpenQuery(query);
		setOpen({});
	}
	const [sort, setSort] = useState<RewardSort>('name');
	const employeeIDs = useMemo(
		() => functions.flatMap((row) => row.employees.map((employee) => employee.id)),
		[functions],
	);
	// Photos are not in the analytics snapshot, so load them from the user directory.
	const { data: avatarData } = graphql.useScoreManagerAvatarsQuery({
		variables: { ids: employeeIDs },
		skip: employeeIDs.length === 0,
	});
	const avatarByID = useMemo(() => {
		const avatars = new Map<string, { source: { url: string } }>();

		avatarData?.usersByIDs.forEach((user) => {
			if (user.avatar?.source.url) {
				avatars.set(user.id, { source: { url: user.avatar.source.url } });
			}
		});

		return avatars;
	}, [avatarData]);
	const rows = useMemo(
		() =>
			[...functions].sort((left, right) =>
				compareRewardFunctions(left, right, sort, i18n.language),
			),
		[functions, i18n.language, sort],
	);
	const domain = useMemo(() => salaryDomain(functions), [functions]);
	const visible = useMemo(
		() =>
			rows.flatMap((row) => {
				if (!query.trim()) {
					return [{ row, personMatch: false }];
				}

				const match = matchRewardRow(row.name, row.employees, query);

				return match ? [{ row, personMatch: match === 'person' }] : [];
			}),
		[query, rows],
	);
	const expandedFor = (id: string, personMatch: boolean) => open[id] ?? personMatch;
	const allOpen =
		visible.length > 0 && visible.every((item) => expandedFor(item.row.id, item.personMatch));

	if (functions.length === 0) {
		return (
			<Empty.Card
				icon={<CashIcon />}
				title={t('analytics.reward.empty.title')}
				description={t('analytics.reward.empty.description')}
			/>
		);
	}

	return (
		<Card>
			<FlexGap alignItems="center" justifyContent="space-between" gap={tokens.gap.list}>
				<Heading3>{t('analytics.reward.title')}</Heading3>
				<FlexGap alignItems="center" gap={tokens.gap.list}>
					<SegmentedControl
						value={sort}
						options={[
							{ title: t('analytics.reward.sort.name'), value: 'name' },
							{ title: t('analytics.reward.sort.deviation'), value: 'deviation' },
							{
								title: t('analytics.reward.sort.salaryDesc'),
								trailingIcon: <ArrowDownIcon />,
								value: 'salary-desc',
							},
							{
								title: t('analytics.reward.sort.salaryAsc'),
								trailingIcon: <ArrowUpIcon />,
								value: 'salary-asc',
							},
						]}
						onChange={(value) => setSort(value as RewardSort)}
					/>
					<Button
						small
						type="secondary"
						icon={allOpen ? <DoubleChevronUpIcon /> : <DoubleChevronDownIcon />}
						title={t(allOpen ? 'analytics.reward.collapseAll' : 'analytics.reward.expandAll')}
						aria-label={t(allOpen ? 'analytics.reward.collapseAll' : 'analytics.reward.expandAll')}
						onClick={() =>
							setOpen(Object.fromEntries(visible.map((item) => [item.row.id, !allOpen])))
						}
					/>
				</FlexGap>
			</FlexGap>
			<ColumnHeader
				labels={[
					t('analytics.reward.genderGap.column.function'),
					t('analytics.reward.column.positions'),
				]}
			/>
			<FunctionRows mx={-3} mb={-3}>
				{visible.length === 0 && (
					<Paragraph small px={3} py={3} style={{ color: tokens.text.tertiary }}>
						{t('analytics.reward.noResults')}
					</Paragraph>
				)}
				{visible.map(({ row, personMatch }) => {
					const expanded = expandedFor(row.id, personMatch);

					return (
						<Accordion
							key={row.id}
							borderless
							noPadding
							isExpanded={expanded}
							onClick={() => setOpen((current) => ({ ...current, [row.id]: !expanded }))}
							customHeader={
								<FunctionRow
									px={3}
									alignItems="center"
									gap={tokens.gap.list}
									style={{
										paddingTop: tokens.padding.container,
										paddingBottom: tokens.padding.container,
									}}
								>
									<Box
										style={{
											display: 'flex',
											flexShrink: 0,
											color: tokens.icon.tertiary,
											transform: expanded ? 'rotate(90deg)' : undefined,
											transition: 'transform 120ms ease-out',
										}}
									>
										<ChevronRightIcon size={16} />
									</Box>
									<Box flex="1 1 160px" minWidth={0} maxWidth={280}>
										<Label>{row.name}</Label>
										<RangeLabel range={row.range} />
									</Box>
									<RangeColumn
										label={row.name}
										range={row.range}
										employees={row.employees}
										domain={domain}
										emptyLabel={t('analytics.reward.noSalary')}
										fullTimeLabel={t('analytics.reward.scale.fullTime')}
									/>
								</FunctionRow>
							}
						>
							<Box
								pr={3}
								style={{
									paddingLeft: tokens.padding.container,
									paddingBottom: tokens.padding.container,
								}}
							>
								{row.employees.map((employee) => (
									<FlexGap
										key={employee.id}
										alignItems="center"
										gap={tokens.gap.list}
										py={`${tokens.padding.container}px`}
										style={{
											opacity: personMatch && !personMatches(employee, query) ? 0.4 : undefined,
										}}
									>
										<Box width={16} flexShrink={0} />
										<FlexGap
											flex="1 1 160px"
											minWidth={0}
											maxWidth={280}
											alignItems="center"
											gap={tokens.gap.element}
										>
											<Avatar
												size={32}
												flexShrink={0}
												user={{
													firstName: employee.firstName,
													lastName: employee.lastName,
													avatar: avatarByID.get(employee.id),
												}}
											/>
											<Box minWidth={0}>
												<Paragraph
													color="antracite"
													fontWeight="medium"
													style={{ lineHeight: tokens.typography.lineHeights.heading }}
												>
													{employee.name}
												</Paragraph>
												<SalaryLabel employee={employee} />
											</Box>
										</FlexGap>
										<RangeColumn
											label={employee.name}
											range={employee.range}
											employees={[employee]}
											domain={domain}
											emptyLabel={t('analytics.reward.noSalary')}
											fullTimeLabel={t('analytics.reward.scale.fullTime')}
										/>
									</FlexGap>
								))}
							</Box>
						</Accordion>
					);
				})}
			</FunctionRows>
		</Card>
	);
}

RewardFunctions.Skeleton = function RewardFunctionsSkeleton() {
	return (
		<Card>
			<Heading3.Skeleton width={160} />
			<Box mt={3}>
				<Accordion.Skeleton borderless />
				<Accordion.Skeleton borderless />
				<Accordion.Skeleton borderless />
			</Box>
		</Card>
	);
};
